// ----------------------------------------------------------------------
// Dato CMS Translation Plugin
//
// Translator plugin file.
// - Prepares the translation request from Dato ( using itemAccesor to get values )
// - Run the request ( in fact multiple requests processing chunks of data )
// - Updates the fields from request response ( using itemAccesor to update values )
// ----------------------------------------------------------------------
// translator.ts
// Oeil Pour Oeil - 2025

import { TranslationRequest } from "./translationService";
import { FieldUpdaterArray } from "./itemAccessorTypes";
import { Configuration } from "./types";
import async from "async";
import translationService from "./translationService";

const chunks = (array: Array<any>, chunkSize: number) => {
  const chunkedArr = [];
  let counter = 0;
  while (counter < array.length) {
    chunkedArr.push(array.slice(counter, counter + chunkSize));
    counter += chunkSize;
  }
  return chunkedArr;
};

type UpdateFieldTask = (
  fields: FieldUpdaterArray,
  fn: async.ErrorCallback | undefined,
) => Promise<void>;

type MakeUpdateFieldsTask = ({
  translate,
  source,
  target,
}: {
  translate: TranslationRequest;
  source: string;
  target: string;
}) => UpdateFieldTask;

const makeUpdateFieldsTask: MakeUpdateFieldsTask = ({
  translate,
  source,
  target,
}) => {
  return async (fields, fn) => {
    const content = fields.reduce((acc, field) => {
      const value = field.valueByLocale(source);
      if (value) acc.push(value);
      return acc;
    }, [] as Array<string>);

    const result = translate(content).then((translations) =>
      Promise.all(
        fields.map(async (field, index) => {
          const text = translations[index];
          return field.updateDatoValue(source, target, text);
        }),
      )
        .then(() => (fn ? fn() : Promise.resolve()))
        .catch((e) => (fn ? fn(e) : Promise.reject(e))),
    );

    return result;
  };
};

const filterValidFieldUpdaters = (
  fields: FieldUpdaterArray,
  override: boolean,
  source: string,
  target: string,
): FieldUpdaterArray =>
  fields.filter((field) => {
    const sourceContent = field.valueByLocale(source);
    if (!sourceContent || sourceContent.trim().length === 0) {
      return false;
    } else if (!override) {
      const targetContent = field.valueByLocale(target);
      return !(targetContent && targetContent.trim().length > 0);
    } else return true;
  });

export type Translator = (
  {
    source,
    target,
    extraLanguages,
    override,
    useChatGPT,
    batchTranslations,
    translateAnswers
  }: {
    source: string;
    target: string;
    extraLanguages: string[];
    override: boolean;
    useChatGPT: boolean;
    batchTranslations: boolean;
    translateAnswers: boolean;
  },
  fn: async.ErrorCallback,
) => void;

export type MakeTranslator = ({
  params,
  fields,
}: {
  params: Configuration;
  fields: FieldUpdaterArray;
}) => Translator;

// Instantiates the translation service given the parameters passed from Dato
const makeTranslator: MakeTranslator = ({ params, fields }) => {
  // Creates a translation request builder from configuration ( set in Dato Plugin Configuration Panel)
  const translationRequestBuilder =
    translationService.makeTranslationRequestBuilder({
      apiUrl: params.apiUrl,
      apiKey: params.apiKey,
    });

  return async (
    { source, target, extraLanguages, override, useChatGPT, batchTranslations, translateAnswers },
    fn,
  ) => {
    var languages: Array<string> = [];

    // If batchTranslations is true, will process all languages set in the batch languages control
    // Else only the target language set in target language control will be processed

    if (batchTranslations) {
      for (const languageIndex in extraLanguages) {
        const lang = extraLanguages[languageIndex];
        if (lang !== source && lang !== target) {
          languages = languages.concat(lang);
        }
      }
    } else {
      languages = [target];
    }

    // If translateAnswers is true, the answers associated to the question in the dato model will be translated
    if (translateAnswers) {
      // transateAnswer is passed as a param and handled by the server.
    }

    // Iterates through languages
    // - Creates updaters with current language for valid fields
    // - Limit in chunks to limit requests size ( or we egt a timeout from dato)
    // - send async update task for each fields chunks

    for (const languageIndex in languages) {
      const language = languages[languageIndex];

      const validFieldUpdaters = filterValidFieldUpdaters(
        fields,
        override,
        source,
        language,
      );

      const fieldUpdatersChunks = chunks(validFieldUpdaters, 50);

      const translationRequest = translationRequestBuilder(
        source,
        language,
        useChatGPT,
        batchTranslations,
        translateAnswers
      );

      // Make the update block that will be executed asynchronously

      // translate:
      // source: original language code
      // target: desired translation language code

      const fieldsUpdateTask = makeUpdateFieldsTask({
        translate: translationRequest,
        source,
        target: language,
      });

      async.forEachLimit(
        fieldUpdatersChunks,
        params.maxRequest,
        fieldsUpdateTask,
        fn,
      );
    }
  };
};

export default makeTranslator;
